/**
 * Config tab — run configuration, stage models, pipeline settings.
 *
 * Data loaded on-demand from fetchRunConfig() when the tab is first activated.
 */

import { esc } from '../../../shared/rendering.js'
import { fetchRunConfig } from '../../../api/run.js'
import type { RunConfig } from '../../../types/api.js'

let _loaded = false

/** Render the Config tab content. */
export function renderConfigTab(runId?: string): string {
  if (runId) {
    _loaded = false
    setTimeout(() => {
      void loadConfig(runId)
    }, 0)
  }

  return '<div id="config-tab-content"><div class="loading"><div class="spinner"></div></div></div>'
}

async function loadConfig(runId: string): Promise<void> {
  if (_loaded) {
    return
  }
  const container = document.getElementById('config-tab-content')
  if (!container) {
    return
  }

  try {
    const config = await fetchRunConfig(runId)
    _loaded = true
    container.innerHTML = renderConfig(config)
  } catch (err) {
    container.innerHTML = `<div class="empty">Failed to load config. ${esc(String(err))}</div>`
  }
}

function renderConfig(cfg: RunConfig): string {
  const notCaptured = 'Not captured in run snapshot'
  const generalRows: [string, string][] = [
    ['Model', cfg.model || notCaptured],
    ['Max Iterations', String(cfg.max_iterations)],
    ['Max Tokens', cfg.max_tokens !== null ? cfg.max_tokens.toLocaleString() : 'Runtime default'],
    ['Work Item', cfg.work_item_id],
    ['Branch', cfg.branch ?? 'Not linked'],
    ['Worktree', cfg.worktree ?? 'Not linked'],
  ]

  const stageModelRows: [string, string][] = Object.entries(cfg.stage_models).map(
    ([stage, model]) => [stage, model],
  )

  const pipelineRows: [string, string][] = [
    ['Gate threshold', cfg.pipeline.gate_threshold],
    ['BDD', cfg.pipeline.bdd_enabled ? 'Enabled' : 'Disabled for this run'],
    ['Semlint', cfg.pipeline.semlint_enabled ? 'Enabled' : 'Disabled for this run'],
    ['Semlint script', cfg.pipeline.semlint_script ?? 'Not configured'],
    ['Introspection', cfg.pipeline.introspect_enabled ? 'Enabled' : 'Disabled for this run'],
  ]

  const toolBadges =
    cfg.tools.length > 0
      ? cfg.tools
          .map((t) => {
            const isMod = ['Edit', 'Write', 'Bash', 'NotebookEdit'].includes(t)
            return `<span class="tt${isMod ? ' mod' : ''}">${esc(t)}</span>`
          })
          .join('')
      : `<span class="cfg-v">${esc(notCaptured)}</span>`

  const hcRows: [string, string][] = [
    ['Dataset', cfg.honeycomb_dataset ?? 'Not configured'],
    ['Team', cfg.honeycomb_team ?? 'Not configured'],
  ]

  return `
    <div class="sl">Run Configuration</div>
    <div class="empty" style="margin-bottom:10px;text-align:left">Immutable run snapshot for reproducibility. Missing values are labelled explicitly instead of shown as dashes.</div>
    ${renderCfgSection('General', generalRows)}
    ${renderCfgSection('Stage Models', stageModelRows)}
    ${renderCfgSection('Pipeline Config', pipelineRows)}
    <div class="pcard cfg-section">
      <div class="pcard-t">Tools Allowed</div>
      <div style="display:flex;gap:3px;flex-wrap:wrap;margin-top:4px">${toolBadges}</div>
    </div>
    ${renderCfgSection('Honeycomb', hcRows)}`
}

function renderCfgSection(title: string, rows: readonly [string, string][]): string {
  const gridHtml =
    rows.length > 0
      ? rows
          .map(
            ([k, v]) => `<span class="cfg-k">${esc(k)}</span><span class="cfg-v">${esc(v)}</span>`,
          )
          .join('')
      : '<span class="cfg-v">Not captured in run snapshot</span>'

  return `<div class="pcard cfg-section">
    <div class="pcard-t">${esc(title)}</div>
    <div class="cfg">${gridHtml}</div>
  </div>`
}
